import { FC, useMemo } from "react";
import { t } from "i18next";
import { useSelector } from "react-redux";
import { useOutletContext } from "react-router";

import { usePricing } from "@hooks/usePricing/usePricing";
import { PricingProps } from "@models/components/booking.model";
import { getLocationType, locationType } from "@models/services/organisation";
import { applyLocalization } from "@services/common.service";
import { RootState } from "@store/index";

import { PaymentMethodSelector } from "../PaymentMethodSelector/PaymentMethodSelector";
import { PriceSummary } from "../PriceSummary/PriceSummary";

const Pricing: FC<PricingProps> = ({ isFromDrawer = false, payment }) => {
  const { getPriceByResourceSlot, priceLoader } = usePricing();
  const { selectedPaymentMethod, setSelectedPaymentMethod } = payment;

  const { paymentLoader, resourcePrices, selectedSlot } = useSelector(
    (State: RootState) => State.booking
  );

  const currency = resourcePrices?.currency || "";
  const vat = resourcePrices?.vat;

  const context: [getLocationType, locationType] = useOutletContext();
  const { resourceId, slot } = selectedSlot;

  const paymentOptions =
    resourcePrices?.prices?.[resourceId]?.paymentOptions ?? [];

  const { basePrice, guestPriceAmount, totalPriceWithTax, vatAmount } =
    useMemo(() => {
      const {
        guestPrice = 0,
        price = 0,
        totalPriceWithTax = 0,
        vatAmount = 0,
      } = getPriceByResourceSlot(resourcePrices, resourceId, slot?.slot) || {};

      const numPrice = Number(price);
      const numGuestPrice = Number(guestPrice);

      return {
        basePrice: isNaN(numPrice) ? 0 : numPrice,
        guestPriceAmount: isNaN(numGuestPrice) ? 0 : numGuestPrice,
        totalPriceWithTax,
        vatAmount,
      };
    }, [resourcePrices, resourceId, slot?.slot, getPriceByResourceSlot]);

  const disablePaymentOption =
    !(basePrice + guestPriceAmount) || paymentLoader || priceLoader;

  const resourceLabel = useMemo(
    () => applyLocalization(context?.[1]?.resourceType?.terminology?.singular),
    [context]
  );

  if (paymentLoader && priceLoader) {
    return (
      <div className="fg-tab-unit-list">
        <div className="text-center py-4">
          <p>{t("loadingPricing")}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {basePrice + guestPriceAmount > 0 && paymentOptions?.length > 0 && (
        <PaymentMethodSelector
          paymentOptions={paymentOptions}
          selectedPaymentMethod={selectedPaymentMethod}
          disablePaymentOption={disablePaymentOption}
          onSelectPaymentMethod={setSelectedPaymentMethod}
        />
      )}

      <PriceSummary
        resourceLabel={resourceLabel}
        basePrice={basePrice}
        guestPrice={guestPriceAmount}
        vatAmount={vatAmount}
        totalPriceWithTax={totalPriceWithTax}
        currency={currency}
        vatDetails={vat}
        isFromDrawer={isFromDrawer}
      />
    </>
  );
};

export default Pricing;
